/**
 * Reading a fund's run of values.
 *
 * Presentation arithmetic over the series the platform sent: rebasing it to
 * ten thousand rupees, measuring how far it sits below its own peak,
 * finding its highest and lowest point. None of this is an indicator -- an
 * indicator is defined in the platform, once -- and all of it is the same
 * kind of thing as rebasing two prices to a shared first session, which
 * the comparison chart already does here.
 */

import type { FundGroup, RollingReturn, SchemeValue } from "@/api/client";
import { drawdown } from "@/lib/drawdown";
import { ABSENT, toNumber } from "@/lib/format";

/** A value on a day, parsed. */
export interface Reading {
  day: string;
  value: number;
}

/** The starting sum a growth series is drawn from. */
export const STAKE = 10_000;

/**
 * Parse the published values, dropping any that will not.
 *
 * @param values - The values as published, oldest first.
 * @returns The readings.
 */
export function readings(values: SchemeValue[]): Reading[] {
  return values.flatMap((one) => {
    const value = toNumber(one.nav);
    return value === null ? [] : [{ day: one.nav_date, value }];
  });
}

/**
 * What ten thousand rupees put in on the first day would be worth since.
 *
 * The oldest way of showing a fund and still the clearest: a rupee sum a
 * reader can feel, rather than a percentage or a unit value that means
 * nothing on its own.
 *
 * @param held - The readings, oldest first.
 * @returns The sum on each day, or nothing when the first value is nought
 *   -- which no unit is worth, and which would make every point infinite.
 */
export function growthOfStake(held: Reading[]): Reading[] {
  const first = held[0];
  if (first === undefined || first.value <= 0) {
    return [];
  }
  return held.map((one) => ({ day: one.day, value: (one.value / first.value) * STAKE }));
}

/** The highest and lowest readings, and the deepest fall. */
export interface Extremes {
  high: Reading;
  low: Reading;
  /**
   * The deepest drawdown, as a reading on the day it bottomed. Nought on
   * the first day for a series that never fell below a peak.
   */
  deepest: Reading;
}

/**
 * Where the series peaked, troughed, and fell furthest.
 *
 * @param held - The readings, oldest first.
 * @returns The extremes, or null for an empty series.
 */
export function extremes(held: Reading[]): Extremes | null {
  const first = held[0];
  if (first === undefined) {
    return null;
  }
  let high = first;
  let low = first;
  for (const one of held) {
    if (one.value > high.value) {
      high = one;
    }
    if (one.value < low.value) {
      low = one;
    }
  }
  let deepest: Reading = { day: first.day, value: 0 };
  for (const one of drawdown(held)) {
    if (one.value < deepest.value) {
      deepest = one;
    }
  }
  return { high, low, deepest };
}

/** How a rolling series was distributed. */
export interface RollingSummary {
  best: Reading;
  worst: Reading;
  median: number;
  /** The share of days on which the year's return was positive, in per cent. */
  positive: number;
}

/**
 * Summarise the rolling one-year returns.
 *
 * A trailing return is one draw; this says what the draws looked like. A
 * fund whose year has ranged from minus thirty to plus sixty is a different
 * holding from one whose year has ranged from four to twelve, however alike
 * their latest figures.
 *
 * @param rolling - The rolling returns, oldest first.
 * @returns The summary, or null when there is nothing to summarise.
 */
export function summariseRolling(rolling: RollingReturn[]): RollingSummary | null {
  const held = rolling.flatMap((one) => {
    const value = toNumber(one.percent);
    return value === null ? [] : [{ day: one.nav_date, value }];
  });
  const first = held[0];
  if (first === undefined) {
    return null;
  }
  let best = first;
  let worst = first;
  let positive = 0;
  for (const one of held) {
    if (one.value > best.value) {
      best = one;
    }
    if (one.value < worst.value) {
      worst = one;
    }
    if (one.value > 0) {
      positive += 1;
    }
  }
  const sorted = held.map((one) => one.value).sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 1
      ? (sorted[middle] ?? 0)
      : ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
  return { best, worst, median, positive: (positive / held.length) * 100 };
}

/** One calendar year's return. */
export interface CalendarYear {
  year: number;
  /** The return over the year, in per cent. */
  value: number;
  /** Whether the year is still running: its return is to the latest value, not to December. */
  toDate: boolean;
}

/**
 * The return in each calendar year the values cover.
 *
 * A year runs from the last value of the year before to its own last
 * value, so the first year in the values is left out: it has no year-end
 * to start from, and a return from whatever day the window opened on is
 * not that year's. The last year is marked to date unless its values reach
 * the final week of December.
 *
 * @param held - The readings, oldest first.
 * @returns Each year's return, oldest first.
 */
export function calendarYears(held: Reading[]): CalendarYear[] {
  const closes = new Map<number, Reading>();
  for (const one of held) {
    closes.set(Number(one.day.slice(0, 4)), one);
  }
  const years = [...closes.keys()].sort((a, b) => a - b);
  const last = years.at(-1);
  return years.flatMap((year) => {
    const before = closes.get(year - 1);
    const close = closes.get(year);
    if (before === undefined || close === undefined || before.value <= 0) {
      return [];
    }
    return [
      {
        year,
        value: (close.value / before.value - 1) * 100,
        toDate: year === last && close.day < `${String(year)}-12-24`,
      },
    ];
  });
}

/**
 * AMFI's category label, shorn of its wrapper.
 *
 * "Open Ended Schemes(Equity Scheme - Large Cap Fund)" is "Large Cap Fund"
 * to anybody reading a table; the wrapper is the same on nine schemes in
 * ten and says nothing about this one.
 *
 * @param category - The label as published.
 * @returns The part that distinguishes it, or a dash.
 */
export function shortCategory(category: string | null): string {
  if (category === null) {
    return ABSENT;
  }
  // To the last bracket, not the first: "(Exchange Traded Funds (ETFs) -
  // Equity ETF)" holds a bracket of its own, and stopping at it wrote
  // "Exchange Traded Funds (ETFs".
  const inner = /\((.*)\)/.exec(category)?.[1] ?? category;
  return inner
    .slice(inner.lastIndexOf(" - ") + 1)
    .replace(/^- /, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * An option as a table says it: "Growth Option" is "Growth".
 *
 * @param option - The option as published.
 * @returns The option without the word.
 */
export function shortOption(option: string): string {
  return option.replace(/\s*option\s*$/i, "").trim();
}

/**
 * What each group of funds is called, in the platform's order. The groups
 * themselves, and which AMFI categories fall in each, are the platform's
 * (`artha.domain.fund_category`); only the words are decided here.
 */
export const FUND_GROUPS: Record<FundGroup, string> = {
  large_cap: "Large cap",
  large_and_mid_cap: "Large & mid cap",
  mid_cap: "Mid cap",
  small_cap: "Small cap",
  flexi_cap: "Flexi cap",
  elss: "Tax saving (ELSS)",
  sectoral: "Sectoral & thematic",
  index: "Index funds & ETFs",
  debt: "Debt",
  hybrid: "Hybrid",
  gold: "Gold",
  international: "International",
};

/**
 * Whether a value names one of the groups.
 *
 * @param value - A value from the address.
 * @returns True for a group's key.
 */
export function isFundGroup(value: string): value is FundGroup {
  return Object.hasOwn(FUND_GROUPS, value);
}
